import moment from 'moment/moment';
import {useCallback, useMemo} from 'react';
import {momentToDateTime} from 'webapp-lib/utils';

import {TimeEntryResponse} from '@lexian/api/timetracker-api';

import {TimerFacade, TimerStartData} from '../timetracker.types';

import {UseTimeEntriesPerDayProps} from './useTimeEntriesPerDay';

type TimerPersistence = Pick<
  UseTimeEntriesPerDayProps,
  'createTimeEntry' | 'updateTimeEntry'
>;

export function useTimer(
  ongoingTimeEntry: TimeEntryResponse | undefined,
  persistence: TimerPersistence
): {
  timer: TimerFacade;
} {
  const {createTimeEntry, updateTimeEntry} = persistence;

  const stopTimer = useCallback(async () => {
    if (!ongoingTimeEntry) {
      return;
    }

    await updateTimeEntry(ongoingTimeEntry.id, {
      casusId: ongoingTimeEntry.casus?.id,
      title: ongoingTimeEntry.title,
      description: ongoingTimeEntry.description,
      startTime: momentToDateTime(moment(ongoingTimeEntry.startTime)),
      endTime: momentToDateTime(moment(new Date())),
    });
  }, [ongoingTimeEntry, updateTimeEntry]);

  const startTimer = useCallback(
    async (data: TimerStartData) => {
      await stopTimer();

      await createTimeEntry({
        casusId: data.casus?.id,
        title: data.title,
        description: data.description,
        startTime: momentToDateTime(moment(new Date())),
        endTime: undefined,
      });
    },
    [stopTimer, createTimeEntry]
  );

  const timer: TimerFacade = useMemo(
    () => ({
      start: startTimer,
      stop: stopTimer,
    }),
    [startTimer, stopTimer]
  );

  return {timer};
}
